// 组合动作合成相关的纯函数：
//   - 从操作记录中解析动作数据（mcpData / params）
//   - 计算动作时长
//   - 将多个动作按顺序合成为一条连续动作（buildCombined）

export const STEP_MS = 20 // 每步 20ms

const roundNum6 = (num, digits = 6) => {
  const n = Number(num)
  if (!isFinite(n)) return 0
  return Number(n.toFixed(digits))
}

const sumSteps = (m) => (Array.isArray(m) ? m : []).reduce((a, seg) => a + (seg && seg.steps ? seg.steps : 0), 0)

// 三次曲线求值（系数形式 [k3, k2, k1, k0]）
const evalCubic = (k3, k2, k1, k0, x) => {
  const r = k3 * x * x * x + k2 * x * x + k1 * x + k0
  return isNaN(r) || !isFinite(r) ? 0 : r
}

// mcpData.motion 中的 param 存在两种历史格式，需兼容：
//   - 4 元系数：[k3, k2, k1, k0]
//   - 6 元：   [start, end, k3, k2, k1, k0]
// segEndValue / segStartValue 返回“片段末尾/起始”在系数空间中的值
const segEndValue = (param, steps) => {
  if (!Array.isArray(param)) return 0
  if (param.length >= 6) return Number(param[1]) || 0
  if (param.length === 4) return evalCubic(param[0], param[1], param[2], param[3], steps)
  return Number(param[param.length - 1]) || 0
}
const segStartValue = (param) => {
  if (!Array.isArray(param)) return 0
  if (param.length >= 6) return Number(param[0]) || 0
  if (param.length === 4) return Number(param[3]) || 0 // k0
  return Number(param[0]) || 0
}

// 生成与源片段格式一致的“保持不动”片段（在系数值 value 上停留 steps 步）
const makeCoeffHold = (steps, value, sampleParam) => {
  const v = roundNum6(value)
  if (Array.isArray(sampleParam) && sampleParam.length >= 6) {
    // [start, end, k3, k2, k1, k0]：常量曲线 => 曲线系数为 0，start = end = v
    return { steps, param: [v, v, 0, 0, 0, v] }
  }
  // [k3, k2, k1, k0]：常量曲线 => [0, 0, 0, v]
  return { steps, param: [0, 0, 0, v] }
}

// 相对角度片段的“保持不动”：param = [start, end, y1/3, y2/3]
const makeRelHold = (steps, relValue) => {
  const v = roundNum6(relValue)
  return { steps, param: [v, v, v, v] }
}
const relEndValue = (param) => (Array.isArray(param) && param.length >= 2) ? (Number(param[1]) || 0) : 0
const relStartValue = (param) => (Array.isArray(param) && param.length >= 1) ? (Number(param[0]) || 0) : 0

const cloneSeg = (seg) => ({ steps: seg.steps, param: Array.isArray(seg.param) ? [...seg.param] : [] })

// 每片段步长为 20ms，动作时长 = 循环次数 × 总步数 × 20ms，取所有舵机中的最大值
export const calcMotionDurationMs = (motionArr) => {
  if (!Array.isArray(motionArr)) return 0
  let max = 0
  motionArr.forEach(servo => {
    const loops = servo.l || 1
    const steps = (servo.m || []).reduce((sum, m) => sum + (m && m.steps ? m.steps : 0), 0)
    const t = loops * steps * STEP_MS
    if (t > max) max = t
  })
  return max
}

// 从记录的 mcpData 中解析出可下发的载荷对象与动作时长
export const parseRecordMotion = (rec) => {
  let mcpInner = null
  let durationMs = 0
  let motionArr = null
  try {
    if (rec.mcpData) {
      const parsed = typeof rec.mcpData === 'string' ? JSON.parse(rec.mcpData) : rec.mcpData
      mcpInner = parsed && parsed.mcpData ? parsed.mcpData : parsed
      const motion = mcpInner && mcpInner.params && mcpInner.params.arguments
        ? mcpInner.params.arguments.motion
        : null
      motionArr = typeof motion === 'string' ? JSON.parse(motion) : motion
      durationMs = calcMotionDurationMs(motionArr)
    }
  } catch (err) {
    console.error('解析 mcpData 失败:', err)
    mcpInner = null
    motionArr = null
  }
  return { mcpInner, durationMs, motionArr }
}

// 从记录的 params 中解析舵机参数列表（用于合成记录时保留舵机元信息）
export const parseRecordServoParamList = (rec) => {
  try {
    if (!rec || rec.params === undefined || rec.params === null) return []
    const p = typeof rec.params === 'string' ? JSON.parse(rec.params) : rec.params
    return Array.isArray(p?.servoParamList) ? p.servoParamList : []
  } catch (err) {
    console.error('解析 params 失败:', err)
    return []
  }
}

// 将多个动作按顺序合成为一条连续的动作，同时生成：
//   - motion：下发用（系数形式，兼容 4/6 元 param）
//   - servoParamList：可编辑/还原用（相对角度形式，取自源记录的精确角度）
// 规则：以每个动作内最长舵机的时长为该动作的基准时长，其余舵机补足“保持不动”的步长，
//       确保所有舵机在每个动作边界上保持同步；动作之间的间隔也以“保持不动”的片段填充。
export const buildCombined = (items, { uniform, uniformInterval }) => {
  const actions = items.filter(it => it.hasMotion && Array.isArray(it.motionArr) && it.motionArr.length > 0)
  if (actions.length === 0) return { motion: [], servoParamList: [], totalMs: 0 }

  // 收集所有参与的舵机（按首次出现顺序）
  const order = []
  const seen = new Set()
  actions.forEach(act => act.motionArr.forEach(sv => {
    if (sv && sv.s !== undefined && !seen.has(sv.s)) { seen.add(sv.s); order.push(sv.s) }
  }))

  const findRel = (act, s) => (Array.isArray(act.servoParamList) ? act.servoParamList : []).find(x => x && x.s === s)

  // 初始值：系数空间、相对角度空间，以及片段格式样本（用于生成一致格式的保持片段）
  const initCoeff = {}, initRel = {}, sampleParam = {}
  order.forEach(s => {
    for (const act of actions) {
      const sv = act.motionArr.find(x => x && x.s === s)
      if (sv && Array.isArray(sv.m) && sv.m.length > 0) {
        sampleParam[s] = sv.m[0].param
        initCoeff[s] = segStartValue(sv.m[0].param)
        break
      }
    }
    for (const act of actions) {
      const rv = findRel(act, s)
      if (rv && Array.isArray(rv.m) && rv.m.length > 0) { initRel[s] = relStartValue(rv.m[0].param); break }
    }
    if (initCoeff[s] === undefined) initCoeff[s] = 0
    if (initRel[s] === undefined) initRel[s] = 0
    if (sampleParam[s] === undefined) sampleParam[s] = [0, 0, 0, 0]
  })

  const curCoeff = { ...initCoeff }
  const curRel = { ...initRel }
  const mCoeff = {}, mRel = {}
  order.forEach(s => { mCoeff[s] = []; mRel[s] = [] })

  actions.forEach((act, ai) => {
    // 该动作的基准时长（步数）= 动作内最长舵机的步数
    let maxSteps = 0
    act.motionArr.forEach(sv => {
      const st = (sv.l || 1) * sumSteps(sv.m)
      if (st > maxSteps) maxSteps = st
    })

    order.forEach(s => {
      const sv = act.motionArr.find(x => x && x.s === s)
      const rv = findRel(act, s)
      if (sv && Array.isArray(sv.m) && sv.m.length > 0) {
        const loops = sv.l || 1
        // 展开循环，统一为 l = 1
        for (let i = 0; i < loops; i++) sv.m.forEach(seg => mCoeff[s].push(cloneSeg(seg)))
        const servoSteps = loops * sumSteps(sv.m)
        const lastSeg = sv.m[sv.m.length - 1]
        const endCoeff = segEndValue(lastSeg.param, lastSeg.steps)
        curCoeff[s] = endCoeff
        const pad = maxSteps - servoSteps
        if (pad > 0) mCoeff[s].push(makeCoeffHold(pad, endCoeff, lastSeg.param))

        // 相对角度轨道（优先用源记录的精确角度）
        if (rv && Array.isArray(rv.m) && rv.m.length > 0) {
          for (let i = 0; i < loops; i++) rv.m.forEach(seg => mRel[s].push(cloneSeg(seg)))
          curRel[s] = relEndValue(rv.m[rv.m.length - 1].param)
        } else {
          // 缺少相对角度数据时退化处理：以系数末值填充并保持步数对齐
          for (let i = 0; i < loops; i++) sv.m.forEach(seg => mRel[s].push(makeRelHold(seg.steps, segEndValue(seg.param, seg.steps))))
          curRel[s] = endCoeff
        }
        if (pad > 0) mRel[s].push(makeRelHold(pad, curRel[s]))
      } else {
        // 该动作中不含此舵机：整段保持当前位置
        if (maxSteps > 0) {
          mCoeff[s].push(makeCoeffHold(maxSteps, curCoeff[s], sampleParam[s]))
          mRel[s].push(makeRelHold(maxSteps, curRel[s]))
        }
      }
    })

    // 动作之间的间隔：所有舵机保持当前位置
    if (ai < actions.length - 1) {
      const intervalMs = uniform ? uniformInterval : (act.interval || 0)
      const intSteps = Math.round((intervalMs || 0) / STEP_MS)
      if (intSteps > 0) {
        order.forEach(s => {
          mCoeff[s].push(makeCoeffHold(intSteps, curCoeff[s], sampleParam[s]))
          mRel[s].push(makeRelHold(intSteps, curRel[s]))
        })
      }
    }
  })

  const motion = order.map(s => ({ s, c: mCoeff[s].length, l: 1, m: mCoeff[s] }))

  // 汇总源记录的舵机元信息（name、range 等），保证还原时可编辑
  const metaMap = {}
  items.forEach(it => (it.servoParamList || []).forEach(sv => {
    if (sv && sv.s !== undefined && !metaMap[sv.s]) metaMap[sv.s] = sv
  }))
  const servoParamList = order.map(s => ({ ...(metaMap[s] || {}), s, c: mRel[s].length, l: 1, m: mRel[s] }))

  // 合成后所有舵机步数一致，总时长取其一
  const totalMs = motion.length > 0 ? sumSteps(motion[0].m) * STEP_MS : 0
  return { motion, servoParamList, totalMs }
}
